import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Building2, Mail, Lock, Phone, ArrowLeft, Loader2, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';

export default function LoginPage() {
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // Email/Password form
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  // OTP form
  const [otpIdentifier, setOtpIdentifier] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [mockOtp, setMockOtp] = useState('');

  // Redirect if already logged in - using useEffect for proper handling
  useEffect(() => {
    if (user) {
      if (user.role === 'super_admin') navigate('/superadmin', { replace: true });
      else if (user.role === 'admin') navigate('/admin', { replace: true });
      else navigate('/app', { replace: true });
    }
  }, [user, navigate]);

  // Return loading state while checking auth
  if (user) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  const redirectByRole = (role) => {
    if (role === 'super_admin') navigate('/superadmin');
    else if (role === 'admin') navigate('/admin');
    else navigate('/app');
  };

  const handleEmailAuth = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await api.post('/auth/login', { email, password });
      login(response.data.token, response.data.user);
      toast.success('Welcome back!');
      redirectByRole(response.data.user.role);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleSendOtp = async () => {
    if (!otpIdentifier) { toast.error('Enter email or phone'); return; }
    setLoading(true);
    try {
      const res = await api.post('/auth/otp/send', {
        identifier: otpIdentifier,
        type: otpIdentifier.includes('@') ? 'email' : 'sms'
      });
      setOtpSent(true);
      if (res.data.mock_otp) setMockOtp(res.data.mock_otp);
      toast.success('OTP sent! Check your inbox.');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send OTP');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async () => {
    setLoading(true);
    try {
      const res = await api.post('/auth/otp/verify', { identifier: otpIdentifier, otp_code: otpCode });
      login(res.data.token, res.data.user);
      toast.success('Verified successfully!');
      redirectByRole(res.data.user.role);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Invalid OTP');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      {/* Left Panel - Decorative */}
      <div className="hidden lg:flex lg:w-[45%] relative bg-slate-900 items-center justify-center p-12">
        <div className="absolute inset-0 bg-gradient-to-br from-emerald-900/30 to-slate-900" />
        <div className="relative text-center">
          <div className="w-16 h-16 rounded-2xl bg-emerald-600 flex items-center justify-center mx-auto mb-8">
            <Building2 size={32} className="text-white" />
          </div>
          <h2 className="text-3xl font-bold text-white font-['Outfit'] mb-2">FreeCoffee</h2>
          <p className="text-emerald-400 text-sm font-medium mb-4">Your Free CommunityOS</p>
          <p className="text-slate-400 max-w-sm mx-auto leading-relaxed">
            The modern way to manage your community. From maintenance to payments, all in one place.
          </p>
          <div className="mt-12 grid grid-cols-2 gap-4 max-w-xs mx-auto">
            {['Admin Portal', 'Resident App', 'Online Payments', 'Maintenance Tracking'].map((item, i) => (
              <div key={i} className="bg-slate-800/60 rounded-lg px-3 py-2.5 text-xs text-slate-300 text-center border border-slate-700/50">
                {item}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Right Panel - Form */}
      <div className="flex-1 flex items-center justify-center p-6 bg-slate-50">
        <div className="w-full max-w-[400px]">
          <Link to="/" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700 mb-8">
            <ArrowLeft size={14} /> Back to home
          </Link>

          <h1 className="text-2xl font-bold text-slate-900 font-['Outfit'] mb-1">
            Welcome Back
          </h1>
          <p className="text-sm text-slate-500 mb-6">
            Sign in to your account
          </p>

          <Tabs defaultValue="email" className="w-full">
            <TabsList className="w-full mb-6 bg-slate-100">
              <TabsTrigger value="email" className="flex-1 text-xs" data-testid="tab-email">Email</TabsTrigger>
              <TabsTrigger value="otp" className="flex-1 text-xs" data-testid="tab-otp">OTP</TabsTrigger>
            </TabsList>

            {/* Email/Password Tab */}
            <TabsContent value="email">
              <form onSubmit={handleEmailAuth} className="space-y-4">
                <div>
                  <Label className="text-xs text-slate-600 mb-1.5 block">Email</Label>
                  <div className="relative">
                    <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                    <Input
                      type="email" value={email} onChange={e => setEmail(e.target.value)}
                      placeholder="you@example.com" className="pl-9" required
                      data-testid="email-input"
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-xs text-slate-600 mb-1.5 block">Password</Label>
                  <div className="relative">
                    <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                    <Input
                      type={showPassword ? "text" : "password"} value={password} onChange={e => setPassword(e.target.value)}
                      placeholder="Enter your password" className="pl-9 pr-10" required
                      data-testid="password-input"
                    />
                    <button 
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    >
                      {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                </div>
                <Button type="submit" className="w-full bg-emerald-600 hover:bg-emerald-700 text-white" disabled={loading} data-testid="email-submit-btn">
                  {loading ? <Loader2 size={16} className="animate-spin mr-2" /> : null}
                  Sign In
                </Button>
                <p className="text-center text-xs text-slate-500 mt-3">
                  Community Admin?{' '}
                  <Link to="/signup/admin" className="text-emerald-600 font-medium hover:underline">Register your community</Link>
                </p>
              </form>
            </TabsContent>

            {/* OTP Tab */}
            <TabsContent value="otp">
              <div className="space-y-4">
                <div>
                  <Label className="text-xs text-slate-600 mb-1.5 block">Email or Phone Number</Label>
                  <div className="relative">
                    <Phone size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                    <Input
                      value={otpIdentifier} onChange={e => setOtpIdentifier(e.target.value)}
                      placeholder="email@example.com or +91..." className="pl-9"
                      data-testid="otp-identifier-input"
                    />
                  </div>
                </div>
                {!otpSent ? (
                  <Button onClick={handleSendOtp} className="w-full bg-emerald-600 hover:bg-emerald-700 text-white" disabled={loading} data-testid="send-otp-btn">
                    {loading ? <Loader2 size={16} className="animate-spin mr-2" /> : null}
                    Send OTP
                  </Button>
                ) : (
                  <>
                    {mockOtp && (
                      <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                        <p className="text-xs text-amber-700 font-medium">Mock OTP (for testing): <span className="font-mono text-base">{mockOtp}</span></p>
                      </div>
                    )}
                    <div>
                      <Label className="text-xs text-slate-600 mb-1.5 block">Enter OTP</Label>
                      <Input
                        value={otpCode} onChange={e => setOtpCode(e.target.value)}
                        placeholder="6-digit code" maxLength={6} className="text-center tracking-widest text-lg"
                        data-testid="otp-code-input"
                      />
                    </div>
                    <Button onClick={handleVerifyOtp} className="w-full bg-emerald-600 hover:bg-emerald-700 text-white" disabled={loading} data-testid="verify-otp-btn">
                      {loading ? <Loader2 size={16} className="animate-spin mr-2" /> : null}
                      Verify OTP
                    </Button>
                    <button className="text-xs text-slate-500 hover:text-emerald-600 w-full text-center" onClick={() => { setOtpSent(false); setMockOtp(''); setOtpCode(''); }}>
                      Change number / Resend
                    </button>
                  </>
                )}
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
}
